import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { getOlsAdgSurfaces, assessOlsAdgSingle } from '../../api/client';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import MapMeasureTool from '../../components/MapMeasureTool';
import Cesium3DPreviewModal from '../../components/Cesium3DPreviewModal';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function StatusBadge({ status }) {
    const colors = { penetrated: T.danger, clear: T.safe, outside: T.muted };
    const c = colors[status] || T.muted;
    return (
        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: '#fff', background: c + '33', border: `1px solid ${c}55` }}>
            {(status || '—').toUpperCase()}
        </span>
    );
}

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

// One toggle per surface "section" of the ADG construction, so any single
// piece can be isolated for review/debugging without the rest of the
// picture in the way. Surfaces that exist per runway END (approach,
// take-off climb, OFZ, instrument departure, precision components) get a
// composite `${type}__${desig}` key built dynamically per aerodrome — see
// buildLayerDefs/buildRwyDirEntries — so each direction can be isolated
// independently, mirroring the legacy OLS page's per-RWY layer tree.
const NON_DIRECTIONAL_LAYER_DEFS = [
    { key: 'strip', label: 'Strip (Ch. 3.4)', color: '#2266ee' },
    { key: 'transitional', label: 'Transitional (4.2.2)', color: '#9b59b6' },
    { key: 'transitional_debug', label: 'Transitional debug points/lines', color: '#ff3b9a' },
    { key: 'horizontal', label: 'Horizontal Surface (4.3.2)', color: '#5BA8D9' },
    { key: 'straight_in', label: 'Straight-in Instrument (4.3.3)', color: '#f1c40f' },
];
const DIRECTIONAL_TYPE_META = {
    approach: { label: 'Approach (4.2.1)', color: '#ff9800' },
    takeoff_climb: { label: 'Take-off Climb (4.3.6)', color: '#e91e63' },
    inner_approach: { label: 'Inner Approach (4.2.3)', color: '#e74c3c' },
    balked_landing: { label: 'Balked Landing (4.2.5)', color: '#f39c12' },
    inner_transitional: { label: 'Inner Transitional (4.2.4)', color: '#16a085' },
    instrument_departure: { label: 'Instrument Departure (4.3.5)', color: '#1abc9c' },
    approach_component: { label: 'Precision: Approach Component', color: '#3498db' },
    missed_approach_component: { label: 'Precision: Missed Approach Component', color: '#8e44ad' },
    lower_component: { label: 'Precision: Lower Component', color: '#2ecc71' },
    transitional_component: { label: 'Precision: Transitional Component', color: '#95a5a6' },
};
// Maps a layer "type" key (the part of a layerDefs key before `__${desig}`,
// or the bare key for non-directional surfaces) to its surfaceStyleDefaults
// registry key — lets the legend swatches and the actual rendered colors
// both flow from the same Settings-customizable source.
// straight_in maps to its "lower" variant for the legend's single combined
// swatch, even though rendering itself uses two distinct colors.
const TYPE_TO_STYLE_KEY = {
    strip: 'ols_adg.strip',
    transitional: 'ols_adg.transitional',
    horizontal: 'ols_adg.horizontal',
    straight_in: 'ols_adg.straight_in_lower',
    approach: 'ols_adg.approach',
    takeoff_climb: 'ols_adg_dep.takeoff_climb',
    inner_approach: 'ols_adg_ofz.inner_approach',
    balked_landing: 'ols_adg_ofz.balked_landing',
    inner_transitional: 'ols_adg_ofz.inner_transitional',
    instrument_departure: 'ols_adg_dep.instrument_departure',
    approach_component: 'ols_adg_pa.approach_component',
    missed_approach_component: 'ols_adg_pa.missed_approach_component',
    lower_component: 'ols_adg_pa.lower_component',
    transitional_component: 'ols_adg_pa.transitional_component',
};

// One entry per (runway pair, direction), carrying which of the 4
// direction-scoped categories actually have data for that end (e.g. balked
// landing/precision only exist on precision-classified ends).
function buildRwyDirEntries(result) {
    if (!result) return [];
    const entries = [];
    (result.runway_pairs || []).forEach(p => {
        const [d1, d2] = (p.pair_name || '').split('/');
        [[d1, p.t1_cls], [d2, p.t2_cls]].forEach(([desig, cls]) => {
            if (!desig) return;
            const ofz = (p.ofz && p.ofz[desig]) || {};
            const dep = (p.departure && p.departure[desig]) || {};
            const pa = (p.precision_approach && p.precision_approach[desig]) || {};
            entries.push({
                desig, pairName: p.pair_name, code: p.code, cls,
                basicLayers: ['strip', `approach__${desig}`, 'transitional', ...(dep.takeoff_climb ? [`takeoff_climb__${desig}`] : [])],
                ofzLayers: [
                    ...(ofz.inner_approach ? [`inner_approach__${desig}`] : []),
                    ...(ofz.inner_transitional ? [`inner_transitional__${desig}`] : []),
                    ...(ofz.balked_landing ? [`balked_landing__${desig}`] : []),
                ],
                departureLayers: dep.instrument_departure ? [`instrument_departure__${desig}`] : [],
                precisionLayers: [
                    ...(pa.approach_component ? [`approach_component__${desig}`] : []),
                    ...(pa.missed_approach_component ? [`missed_approach_component__${desig}`] : []),
                    ...(pa.lower_component ? [`lower_component__${desig}`] : []),
                    ...(pa.transitional_component ? [`transitional_component__${desig}`] : []),
                ],
            });
        });
    });
    return entries;
}

function buildLayerDefs(result, styleFor = () => null) {
    const dynamic = buildRwyDirEntries(result).flatMap(e => {
        const keys = [...e.basicLayers, ...e.ofzLayers, ...e.departureLayers, ...e.precisionLayers]
            .filter(k => k.includes('__'));
        return keys.map(key => {
            const type = key.split('__')[0];
            const meta = DIRECTIONAL_TYPE_META[type];
            return { key, label: `${meta.label} — RWY ${e.desig}`, color: styleFor(type) || meta.color };
        });
    });
    const nonDirectional = NON_DIRECTIONAL_LAYER_DEFS.map(l => ({ ...l, color: styleFor(l.key) || l.color }));
    return [...nonDirectional, ...dynamic];
}

// Related surfaces bundled for one-click isolate-and-screenshot/export, one
// group per runway direction for the 4 direction-scoped categories, plus
// the 2 aerodrome-wide surfaces (no direction split — they don't have one).
function buildScreenshotGroups(result) {
    const groups = [];
    buildRwyDirEntries(result).forEach(e => {
        groups.push({ key: `basic__${e.desig}`, label: `RWY ${e.desig} — Strip, Approach, Transitional, Take-off`, layers: e.basicLayers, backendGroup: 'basic', desig: e.desig });
        if (e.ofzLayers.length) groups.push({ key: `ofz__${e.desig}`, label: `RWY ${e.desig} — Inner Approach, Inner Transitional, Balked Landing`, layers: e.ofzLayers, backendGroup: 'ofz', desig: e.desig });
        if (e.departureLayers.length) groups.push({ key: `departure__${e.desig}`, label: `RWY ${e.desig} — Instrument Departure`, layers: e.departureLayers, backendGroup: 'departure', desig: e.desig });
        if (e.precisionLayers.length) groups.push({ key: `precision__${e.desig}`, label: `RWY ${e.desig} — Precision Approach Surface`, layers: e.precisionLayers, backendGroup: 'precision', desig: e.desig });
    });
    groups.push({ key: 'horizontal', label: 'Horizontal Surface', layers: ['horizontal'], backendGroup: 'horizontal', desig: null });
    groups.push({ key: 'straight_in', label: 'Straight-in Instrument Approaches', layers: ['straight_in'], backendGroup: 'straight_in', desig: null });
    return groups;
}

// Manual canvas capture (tiles + SVG overlay + marker icons) — same approach
// as ObstaclePreview.jsx, which avoids the coordinate shift html2canvas
// produces on Leaflet's CSS-transformed tile/pane elements.
async function captureLeafletMap(container) {
    const containerRect = container.getBoundingClientRect();
    const w = Math.round(containerRect.width);
    const h = Math.round(containerRect.height);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#07111e';
    ctx.fillRect(0, 0, w, h);

    const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
    await Promise.all(tileEls.map(async tile => {
        if (!tile.src) return;
        const tRect = tile.getBoundingClientRect();
        const tx = Math.floor(tRect.left - containerRect.left);
        const ty = Math.floor(tRect.top - containerRect.top);
        const tw = Math.ceil(tRect.width) + 1;
        const th = Math.ceil(tRect.height) + 1;
        try {
            const resp = await fetch(tile.src, { mode: 'cors' });
            if (!resp.ok) return;
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            await new Promise(resolve => {
                const img = new Image();
                img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                img.src = url;
            });
        } catch { /* skip broken tiles */ }
    }));

    const svgEl = container.querySelector('.leaflet-overlay-pane svg');
    if (svgEl) {
        const svgRect = svgEl.getBoundingClientRect();
        const sx = Math.round(svgRect.left - containerRect.left);
        const sy = Math.round(svgRect.top - containerRect.top);
        const clone = svgEl.cloneNode(true);
        clone.style.transform = '';
        clone.style.left = '0';
        clone.style.top = '0';
        const svgStr = new XMLSerializer().serializeToString(clone);
        const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        await new Promise(resolve => {
            const img = new Image();
            img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
            img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
            img.src = url;
        });
    }

    const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
    for (const marker of markerEls) {
        const mRect = marker.getBoundingClientRect();
        const x = Math.round(mRect.left - containerRect.left);
        const y = Math.round(mRect.top - containerRect.top);
        const img = marker.querySelector('img');
        if (img && img.complete && img.naturalWidth) {
            try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch { /* ignore */ }
        }
    }

    return canvas.toDataURL('image/png');
}

function downloadDataUrl(dataUrl, filename) {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

export default function OlsAdgPage({ project, arps, obstacles, showObstacles = true, showObsLabels = true, showArpLabels = true, onObstacleContextMenu, onMapReady, onAerodromeContextMenu, onResultsUpdated }) {
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    const tileRef = useRef(null);
    const layersRef = useRef([]);
    const obsLayersRef = useRef([]);

    const { getStyle } = useSurfaceStyles();
    const styleForType = useCallback(type => {
        const fullKey = TYPE_TO_STYLE_KEY[type];
        return fullKey ? getStyle(fullKey).color : null;
    }, [getStyle]);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [icao, setIcao] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [assessResults, setAssessResults] = useState([]);
    const [assessing, setAssessing] = useState(false);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(220, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);
    const [layerVis, setLayerVis] = useState({});
    const [capturingGroup, setCapturingGroup] = useState(null);
    const [exportingAixm, setExportingAixm] = useState(false);

    // Step 2 reference set — this project's own obstacles that actually
    // penetrate something (any surface), not just its tallest point overall.
    const penetratingObstacleIds = useMemo(() => (
        assessResults.filter(r => r.status === 'penetrated').map(r => r.obstacle_id)
    ), [assessResults]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);

    // ── Inclined OLS(ADG) surface shielding — real, not flat ────────────────
    // Same reasoning as OLSPage.jsx: Approach, Transitional, the
    // Precision-Approach OFZ components, Instrument Departure and Take-off
    // Climb are all sloped, so "shields" has to mean "already penetrates
    // THIS surface, at its own position, by at least as much as the
    // project's own worst obstacle does". Horizontal IS flat (a fixed height
    // above ARP), but a project-max/project-worst elevation comparison isn't
    // a correct proxy for it either — a candidate can sit below the
    // project's own obstacles yet still clear Horizontal's own absolute
    // height, or vice versa — so it gets the same real-per-point check too.
    // Reuses assessOlsAdgSingle — the exact engine already used for real
    // project obstacles — for candidates too, so this can never diverge from
    // the real assessment. OLS ADG only tracks one loaded aerodrome at a
    // time (assessResults is a flat array, not per-icao like OLSPage's),
    // which simplifies this block relative to its OLS counterpart.
    const ADG_INCLINED_SURFACES = ['transitional', 'approach', 'pa_approach', 'pa_missed_approach', 'pa_transitional', 'instrument_departure', 'takeoff_climb', 'horizontal'];
    const ADG_SURFACE_LABELS = {
        transitional: 'Transitional', approach: 'Approach',
        pa_approach: 'Precision Approach (OFZ)', pa_missed_approach: 'Precision Missed Approach (OFZ)', pa_transitional: 'Precision Transitional (OFZ)',
        instrument_departure: 'Instrument Departure', takeoff_climb: 'Take-off Climb', horizontal: 'Horizontal Surface (OES)',
    };
    const [adgShieldPens, setAdgShieldPens] = useState([]); // candidate result rows (inclined surfaces only)
    const [adgShieldLoading, setAdgShieldLoading] = useState(false);

    const adgSurfaceImpacts = useMemo(() => {
        const out = {}; // `${surfaceType}_${runway||'OMNI'}` -> worst (most negative) margin
        assessResults.forEach(r => {
            if (!ADG_INCLINED_SURFACES.includes(r.surfaceType) || r.status !== 'penetrated' || r.margin == null) return;
            const key = `${r.surfaceType}_${r.runway || 'OMNI'}`;
            if (!(key in out) || r.margin < out[key]) out[key] = r.margin;
        });
        return out;
    }, [assessResults]);
    const hasAdgInclinedImpact = Object.keys(adgSurfaceImpacts).length > 0;

    useEffect(() => {
        if (!shieldOn || !shieldData || !hasAdgInclinedImpact || !icao) { setAdgShieldPens([]); return; }
        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setAdgShieldPens([]); return; }

        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));

        let cancelled = false;
        (async () => {
            setAdgShieldLoading(true);
            try {
                const { data } = await assessOlsAdgSingle(icao, pseudoObstacles);
                if (!cancelled) setAdgShieldPens((data.results || []).filter(r => ADG_INCLINED_SURFACES.includes(r.surfaceType)));
            } catch (e) {
                console.error(`OLS(ADG) inclined-surface shield check failed for ${icao}`, e);
            } finally {
                if (!cancelled) setAdgShieldLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasAdgInclinedImpact, icao]); // eslint-disable-line

    // Candidate pseudo-id -> [{ surfaceType, runway, margin, projectMargin }, ...]
    const adgShieldVerdicts = useMemo(() => {
        const out = new Map();
        adgShieldPens.forEach(r => {
            if (r.margin == null) return;
            const key = `${r.surfaceType}_${r.runway || 'OMNI'}`;
            const projectMargin = adgSurfaceImpacts[key];
            if (projectMargin == null) return;
            if (r.margin <= projectMargin) {
                const list = out.get(r.obstacle_id) || [];
                list.push({ surfaceType: r.surfaceType, runway: r.runway, margin: r.margin, projectMargin });
                out.set(r.obstacle_id, list);
            }
        });
        return out;
    }, [adgShieldPens, adgSurfaceImpacts]);

    // Candidate pseudo-id -> true if it falls under ANY inclined surface's
    // footprint at all (has a real margin there) — see olsShieldInsideSurface
    // in OLSPage.jsx for why this is kept separate from "shields".
    const adgShieldInsideSurface = useMemo(() => {
        const out = new Map();
        adgShieldPens.forEach(r => { if (r.margin != null) out.set(r.obstacle_id, true); });
        return out;
    }, [adgShieldPens]);

    // Candidate pseudo-id -> [{ surfaceType, runway, margin }, ...] for every
    // surface it genuinely penetrates at its own position (margin < 0),
    // regardless of raw elevation or of beating the project's worst margin —
    // see olsShieldRealPenetration in OLSPage.jsx.
    const adgShieldRealPenetration = useMemo(() => {
        const out = new Map();
        adgShieldPens.forEach(r => {
            if (r.margin == null || r.margin >= 0) return;
            const list = out.get(r.obstacle_id) || [];
            list.push({ surfaceType: r.surfaceType, runway: r.runway, margin: r.margin });
            out.set(r.obstacle_id, list);
        });
        return out;
    }, [adgShieldPens]);

    // Enrich each shield marker's own popup (from useShield) with the real
    // per-surface verdict(s), same pattern as OLSPage.jsx. Also recolours the
    // marker yellow for a genuine real-surface penetration that wasn't
    // already red (raw-elevation shield).
    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real surface
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasAdgInclinedImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = adgShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = adgShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = adgShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v => ADG_SURFACE_LABELS[v.surfaceType] + (v.runway ? ` RWY ${v.runway}` : '')))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">Inclined OLS(ADG) surfaces: ${ADG_SURFACE_LABELS[v.surfaceType]}</b>${v.runway ? ` RWY ${v.runway}` : ''} ` +
                    `(this obstacle's margin ${v.margin.toFixed(1)} m vs project's worst ${v.projectMargin.toFixed(1)} m)`
                  ).join('')
                : `<br/><span style="color:#8a94a6">Inclined OLS(ADG) surfaces: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, adgShieldVerdicts, adgShieldInsideSurface, adgShieldRealPenetration, hasAdgInclinedImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield at
    // least one inclined ADG surface.
    const adgShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (adgShieldLayerRef.current) { mapInst.current.removeLayer(adgShieldLayerRef.current); adgShieldLayerRef.current = null; }
        if (!shieldOn || adgShieldVerdicts.size === 0) return;

        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        adgShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const lines = verdicts.map(v =>
                `<b>${ADG_SURFACE_LABELS[v.surfaceType]}</b>${v.runway ? ` RWY ${v.runway}` : ''} — margin <b>${v.margin.toFixed(1)} m</b> (project worst: ${v.projectMargin.toFixed(1)} m)`
            ).join('<br/>');
            marker.bindPopup(
                `<b>🛫 TRUE SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>` +
                `Aerodrome: ${icao}<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact on ${verdicts.length > 1 ? 'these surfaces' : 'this surface'}.</span>`
            );
            group.addLayer(marker);
        });

        if (group.getLayers().length > 0) {
            adgShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, adgShieldVerdicts, shieldData, icao]);

    const layerDefs = useMemo(() => buildLayerDefs(result, styleForType), [result, styleForType]);
    const screenshotGroups = useMemo(() => buildScreenshotGroups(result), [result]);

    // Reset to "everything visible" whenever a new aerodrome's surfaces load
    // — the set of per-direction keys changes (different designators).
    // transitional_debug is the exception: it's construction-geometry
    // scaffolding for reviewing how the transitional surface was built, not
    // something to show by default — starts off, same as the "All On"
    // button leaves it available to switch on deliberately.
    useEffect(() => {
        setLayerVis(Object.fromEntries(layerDefs.map(l => [l.key, l.key !== 'transitional_debug'])));
    }, [result]); // eslint-disable-line

    const toggleLayer = (key) => setLayerVis(p => ({ ...p, [key]: !p[key] }));
    const setAllLayers = (visible) => setLayerVis(Object.fromEntries(layerDefs.map(l => [l.key, visible])));

    const handleGroupScreenshot = async (group) => {
        if (!mapRef.current || capturingGroup) return;
        setCapturingGroup(group.key);
        // Isolate this group's layers (obstacle visibility is controlled
        // separately by the shared showObstacles toggle, not this tree),
        // then wait a beat for React + Leaflet's DOM to settle before capturing.
        setLayerVis(Object.fromEntries(layerDefs.map(l => [l.key, group.layers.includes(l.key)])));
        await new Promise(r => setTimeout(r, 450));
        try {
            const dataUrl = await captureLeafletMap(mapRef.current);
            downloadDataUrl(dataUrl, `OLS_ADG_${icao || 'aerodrome'}_${group.key}.png`);
        } catch (e) {
            console.error('Group screenshot failed:', e);
            alert('Failed to capture screenshot.');
        } finally {
            setCapturingGroup(null);
        }
    };

    const [exportingGroup, setExportingGroup] = useState(null);

    const [preview3DGroup, setPreview3DGroup] = useState(null);

    const handleGroupKmz = async (group, mode) => {
        if (!icao || exportingGroup) return;
        const exportKey = `${group.key}_${mode}`;
        setExportingGroup(exportKey);
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            let url = `${API_BASE}/ols-adg-surfaces/${icao}/kml/${group.backendGroup}?mode=${mode}`;
            if (group.desig) url += `&desig=${encodeURIComponent(group.desig)}`;
            if (project?.id) url += `&project_id=${project.id}`;
            const response = await fetch(url);
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                throw new Error(body?.detail || `Server responded ${response.status}`);
            }
            const blob = await response.blob();
            const objUrl = window.URL.createObjectURL(blob);
            const fileDesig = group.desig ? `_RWY${group.desig}` : '';
            downloadDataUrl(objUrl, `${icao}_ADG_${group.backendGroup}${fileDesig}_${mode.toUpperCase()}.kmz`);
            window.URL.revokeObjectURL(objUrl);
        } catch (e) {
            console.error('Group KMZ export failed:', e);
            alert(e.message || 'Failed to export KMZ.');
        } finally {
            setExportingGroup(null);
        }
    };

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=ols_adg`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // The server resolves the real aerodrome(s) from obstacle
            // proximity, not project.aerodrome (often stale/left at its
            // default 'FAOR') — read its filename back off the response
            // rather than reconstructing one from that field here too.
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_OLS_ADG_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    const obstacleList = obstacles || [];

    // Same 15 km radius as legacy OLS (OLSPage.jsx) — an aerodrome's ADG
    // surfaces this far from every obstacle in the project aren't a
    // realistic obstacle-clearance concern, so it isn't offered here either.
    const validArps = useMemo(() => {
        const DEG = Math.PI / 180;
        const R = 6371000;
        return (arps || []).filter(arp => {
            if (!arp.lat || !arp.lon) return false;
            for (const obs of obstacleList) {
                if (!obs.latitude || !obs.longitude) continue;
                const dLat = (obs.latitude - arp.lat) * DEG;
                const dLon = (obs.longitude - arp.lon) * DEG;
                const a = Math.sin(dLat / 2) ** 2 + Math.cos(arp.lat * DEG) * Math.cos(obs.latitude * DEG) * Math.sin(dLon / 2) ** 2;
                if (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) <= 15000) return true;
            }
            return false;
        });
    }, [arps, obstacleList]);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    useEffect(() => {
        if (!icao && validArps.length) setIcao(validArps[0].icao);
    }, [validArps, icao]);

    const load = useCallback(async () => {
        if (!icao) return;
        setLoading(true);
        setError('');
        setAssessResults([]);
        try {
            const { data } = await getOlsAdgSurfaces(icao);
            setResult(data);

            if (obstacleList.length > 0) {
                setAssessing(true);
                try {
                    const aRes = await assessOlsAdgSingle(icao, obstacleList);
                    const rows = aRes.data.results || [];
                    setAssessResults(rows);
                    onResultsUpdated?.(icao, rows);
                } catch (ae) {
                    console.error(`OLS (ADG) assess failed for ${icao}:`, ae);
                } finally {
                    setAssessing(false);
                }
            }
        } catch (err) {
            console.error('Failed to load OLS (ADG) strip', err);
            setError(err.response?.data?.detail || err.message);
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [icao, obstacles]); // eslint-disable-line

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => {
            ro.disconnect();
            mapInst.current?.remove();
            mapInst.current = null;
            setMapState(null);
            onMapReady?.(null);
        };
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    useEffect(() => {
        const map = mapInst.current;
        if (!map) return;
        layersRef.current.forEach(l => map.removeLayer(l));
        layersRef.current = [];
        if (!result) return;

        const bounds = [];
        const add = (layer, pushBounds = true) => {
            layer.addTo(map);
            layersRef.current.push(layer);
            if (pushBounds) {
                try {
                    const b = layer.getBounds();
                    if (b.isValid()) { bounds.push(b.getSouthWest()); bounds.push(b.getNorthEast()); }
                } catch (e) {}
            }
        };

        const horizontalStyle = getStyle('ols_adg.horizontal');
        if (layerVis.horizontal && result.horizontal) {
            add(L.geoJSON(result.horizontal, {
                style: { color: horizontalStyle.color, weight: 1.5, dashArray: '4 4', fillColor: horizontalStyle.color, fillOpacity: horizontalStyle.opacity },
            }).bindTooltip(
                `<b>Horizontal Surface</b><br/>ADG: ${result.horizontal.properties.adg || '—'}<br/>Height: ${result.horizontal.properties.height_m} m above AD elevation`,
                { sticky: true }
            ), false);
        }

        if (layerVis.straight_in) {
            if (result.straight_in_instrument?.lower) {
                const s = getStyle('ols_adg.straight_in_lower');
                add(L.geoJSON(result.straight_in_instrument.lower, {
                    style: { color: s.color, weight: 1.5, dashArray: '2 6', fillColor: s.color, fillOpacity: s.opacity },
                }).bindTooltip('<b>Straight-in Instrument Approach (Lower)</b><br/>45 m above AD elevation', { sticky: true }), false);
            }
            if (result.straight_in_instrument?.upper) {
                const s = getStyle('ols_adg.straight_in_upper');
                add(L.geoJSON(result.straight_in_instrument.upper, {
                    style: { color: s.color, weight: 1.5, dashArray: '2 6', fillColor: s.color, fillOpacity: s.opacity },
                }).bindTooltip('<b>Straight-in Instrument Approach (Upper)</b><br/>60 m above AD elevation', { sticky: true }), false);
            }
        }

        const arpLl = [result.aerodrome.lat, result.aerodrome.lon];
        const arpMarker = L.marker(arpLl, { icon: getArpIcon(L, result.aerodrome, showArpLabels) })
            .bindPopup(`<b>${result.aerodrome.icao}</b><br/>${result.aerodrome.name}<br/>ADG: ${result.aerodrome.adg || '—'}`);
        if (onAerodromeContextMenu) {
            arpMarker.on('contextmenu', e => {
                e.originalEvent.preventDefault();
                onAerodromeContextMenu(result.aerodrome, e.originalEvent.clientX, e.originalEvent.clientY);
            });
        }
        add(arpMarker, false);
        bounds.push(arpLl);

        const OFZ_STYLE = {
            inner_approach: getStyle('ols_adg_ofz.inner_approach'),
            balked_landing: getStyle('ols_adg_ofz.balked_landing'),
            inner_transitional: getStyle('ols_adg_ofz.inner_transitional'),
        };
        const PA_STYLE = {
            approach_component: getStyle('ols_adg_pa.approach_component'),
            missed_approach_component: getStyle('ols_adg_pa.missed_approach_component'),
            lower_component: getStyle('ols_adg_pa.lower_component'),
            transitional_component: getStyle('ols_adg_pa.transitional_component'),
        };
        const DEP_STYLE = {
            instrument_departure: getStyle('ols_adg_dep.instrument_departure'),
            takeoff_climb: getStyle('ols_adg_dep.takeoff_climb'),
        };
        const stripStyle = getStyle('ols_adg.strip');
        const approachStyle = getStyle('ols_adg.approach');
        const transitionalStyle = getStyle('ols_adg.transitional');

        result.runway_pairs.forEach(p => {
            if (layerVis.strip) {
                add(L.geoJSON(p.strip, {
                    style: { color: stripStyle.color, weight: 2, fillColor: stripStyle.color, fillOpacity: stripStyle.opacity },
                }).bindTooltip(
                    `<b>RWY ${p.pair_name} — Strip</b><br/>Classification: ${p.classification}<br/>Code: ${p.code} · ADG: ${p.adg || '—'}<br/>Width: ${p.strip.properties.width_m} m${p.strip.properties.aip_override ? ' (AIP)' : ''}`,
                    { sticky: true }
                ));
            }

            (p.approach || []).forEach(feat => {
                const props = feat.properties;
                if (!layerVis[`approach__${props.designation}`]) return;
                add(L.geoJSON(feat, {
                    style: { color: approachStyle.color, weight: 2, fillColor: approachStyle.color, fillOpacity: approachStyle.opacity },
                }).bindTooltip(
                    `<b>${props.name}</b><br/>${props.instrument ? 'Instrument' : 'Non-instrument'} · ADG: ${props.adg || '—'}<br/>Inner width: ${props.inner_w_m} m${props.aip_override ? ' (AIP)' : ''}<br/>Length: ${props.length_m} m · Slope: ${props.slope_pct}%`,
                    { sticky: true }
                ));
            });

            if (layerVis.transitional) {
                (p.transitional || []).forEach(feat => {
                    const props = feat.properties;
                    add(L.geoJSON(feat, {
                        style: { color: transitionalStyle.color, weight: 2, fillColor: transitionalStyle.color, fillOpacity: transitionalStyle.opacity },
                    }).bindTooltip(
                        `<b>${props.name}</b><br/>${props.instrument ? 'Instrument' : 'Non-instrument'} · ADG: ${props.adg || '—'}<br/>Slope: ${props.slope_pct}%`,
                        { sticky: true }
                    ));
                });
            }

            Object.entries(p.ofz || {}).forEach(([desig, surfs]) => {
                Object.entries(surfs).forEach(([type, feat]) => {
                    if (!layerVis[`${type}__${desig}`]) return;
                    const style = OFZ_STYLE[type] || { color: '#fff', opacity: 0.1 };
                    add(L.geoJSON(feat, {
                        style: { color: style.color, weight: 2, fillColor: style.color, fillOpacity: style.opacity },
                    }).bindTooltip(`<b>${feat.properties.name}</b>`, { sticky: true }));
                });
            });

            // Precision approach + departure/takeoff climb span up to
            // ~25 km along the runway — deliberately excluded from
            // fitBounds so the default view stays focused on the close-in
            // surfaces (strip/approach/transitional).
            Object.entries(p.precision_approach || {}).forEach(([desig, surfs]) => {
                Object.entries(surfs).forEach(([type, feat]) => {
                    if (!layerVis[`${type}__${desig}`]) return;
                    const style = PA_STYLE[type] || { color: '#fff', opacity: 0.05 };
                    add(L.geoJSON(feat, {
                        style: { color: style.color, weight: 1.5, dashArray: '3 5', fillColor: style.color, fillOpacity: style.opacity },
                    }).bindTooltip(`<b>${feat.properties.name}</b>`, { sticky: true }), false);
                });
            });

            Object.entries(p.departure || {}).forEach(([desig, surfs]) => {
                Object.entries(surfs).forEach(([type, feat]) => {
                    if (!layerVis[`${type}__${desig}`]) return;
                    const style = DEP_STYLE[type] || { color: '#fff', opacity: 0.05 };
                    add(L.geoJSON(feat, {
                        style: { color: style.color, weight: 1.5, dashArray: '1 4', fillColor: style.color, fillOpacity: style.opacity },
                    }).bindTooltip(`<b>${feat.properties.name}</b>`, { sticky: true }), false);
                });
            });

            if (layerVis.transitional_debug) {
                (p.transitional_debug_lines || []).forEach(feat => {
                    add(L.geoJSON(feat, {
                        style: { color: '#ff3b9a', weight: 2, dashArray: '6 5' },
                    }).bindTooltip(`${feat.properties.label} (${feat.properties.side})`, { sticky: true }), false);
                });

                (p.transitional_debug_points || []).forEach(feat => {
                    const [lon, lat] = feat.geometry.coordinates;
                    const { label, side } = feat.properties;
                    const color = side === 'LEFT' ? '#f1c40f' : '#1abc9c';
                    const ptLayer = L.circleMarker([lat, lon], {
                        radius: 5, color: '#000', weight: 1, fillColor: color, fillOpacity: 1,
                    }).bindTooltip(`${label} (${side})`, {
                        permanent: true, direction: 'top', offset: [0, -6],
                    });
                    add(ptLayer, false);
                    bounds.push([lat, lon]);
                });
            }
        });

        if (bounds.length > 0) {
            try { map.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 13 }); } catch (e) {}
        }
    }, [result, layerVis, onAerodromeContextMenu, getStyle, showArpLabels]);

    useEffect(() => {
        const map = mapInst.current;
        if (!map) return;
        obsLayersRef.current.forEach(l => map.removeLayer(l));
        obsLayersRef.current = [];
        const hullActive = localStorage.getItem(`tsela-hull-mode-${project?.id}`) === 'true';
        if (!showObstacles || hullActive) return;

        const penetratingIds = new Set(
            assessResults.filter(r => r.status === 'penetrated').map(r => Number(r.obstacle_id))
        );
        obstacleList.filter(o => o.latitude && o.longitude).forEach(o => {
            const isPen = penetratingIds.has(Number(o.id));
            const hasResult = assessResults.some(r => Number(r.obstacle_id) === Number(o.id) && r.status !== 'outside');
            const icon = getObstacleIcon(L, o, isPen ? 'penetrated' : hasResult ? 'clear' : null, showObsLabels);

            const obsResults = assessResults.filter(r => Number(r.obstacle_id) === Number(o.id));
            // Strip surfaces report margin: null (Annex 14 6.3.1 — an obstacle
            // inside the strip is an automatic violation, no elevation margin
            // applies) — treat that as worse than any numeric penetration
            // rather than skipping it, or a real strip violation gets hidden
            // behind a merely-clear surface that does have a margin.
            const penOf = r => r.margin != null ? -r.margin : (r.status === 'penetrated' ? Infinity : -Infinity);
            let worstRes = null;
            for (const r of obsResults) {
                if (r.margin == null && r.status !== 'penetrated') continue;
                if (!worstRes || penOf(r) > penOf(worstRes)) worstRes = r;
            }
            const surfaceStr = worstRes ? worstRes.surface : '—';
            const penetrationStr = worstRes ? (worstRes.margin != null ? `${(-worstRes.margin).toFixed(2)} m` : 'Penetrated (Strip)') : '—';

            const marker = L.marker([o.latitude, o.longitude], { icon })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>Elevation: ${o.elevation_m ?? '—'} m<br/>Surface: ${surfaceStr}<br/>Penetration: ${penetrationStr}`);
            if (onObstacleContextMenu) {
                marker.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            marker.addTo(map);
            obsLayersRef.current.push(marker);
        });
        addHullPolygonsToMap(map, obstacleList, project?.id, obsLayersRef);
    }, [result, obstacleList, assessResults, showObstacles, showObsLabels, onObstacleContextMenu]);

    const renderResultsContent = () => (
        <div style={{ height: '100%', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                    <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                        {['Obstacle', 'Runway', 'Surface', 'Surface Elev (m)', 'Obstacle Elev (m)', 'Penetration (m)', 'Status'].map(h => (
                            <th key={h} style={{ padding: '6px 12px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {assessResults.length > 0 ? assessResults.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: r.status === 'penetrated' ? 'rgba(232,64,64,0.10)' : r.status === 'clear' ? 'rgba(46,201,139,0.06)' : 'transparent' }}>
                            <td style={{ padding: '5px 12px', color: T.white }}>{r.obstacle_name}</td>
                            <td style={{ padding: '5px 12px', color: T.muted }}>{r.runway}</td>
                            <td style={{ padding: '5px 12px', color: T.white }}>{r.surface}</td>
                            <td style={{ padding: '5px 12px', color: T.muted }}>{r.surf_elev_amsl ?? '—'}</td>
                            <td style={{ padding: '5px 12px', color: T.muted }}>{r.obs_elev ?? '—'}</td>
                            <td style={{ padding: '5px 12px', color: r.margin == null ? T.muted : r.margin < 0 ? T.danger : T.safe, fontWeight: r.margin != null ? 700 : 400 }}>
                                {r.margin != null ? (-r.margin).toFixed(2) : '—'}
                            </td>
                            <td style={{ padding: '5px 12px' }}><StatusBadge status={r.status} /></td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={7} style={{ padding: '20px 12px', textAlign: 'center', color: T.muted }}>
                                {obstacleList.length === 0 ? 'No obstacles in this project' : assessing ? 'Running assessment…' : 'No surface intersections found'}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div style={{ display: 'flex', flex: 1, height: '100%', minHeight: 0, background: T.bg2, position: 'relative' }}>
            <div style={{ width: 300, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', background: T.bg1, zIndex: 10, flexShrink: 0 }}>
                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        OLS (ADG) — Surfaces
                    </div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                        Strip (Ch. 3.4), Approach (4.2.1), Transitional (4.2.2), Inner Approach (4.2.3), Inner Transitional (4.2.4) and Balked Landing (4.2.5, precision only) — Amendment 18 OFS.
                    </div>

                    <button
                        onClick={handleExportAixm}
                        disabled={exportingAixm}
                        style={{ marginTop: 10, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                        title="Export obstacles, aerodrome data, waypoints and OLS-ADG surfaces (ObstacleArea) as AIXM 5.1"
                    >
                        {exportingAixm ? '⏳…' : '📤 AIXM'}
                    </button>

                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}33` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Layers ({Object.values(layerVis).filter(Boolean).length}/{layerDefs.length})
                            </span>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => setAllLayers(true)} style={{ padding: '2px 8px', fontSize: 10, fontWeight: 700, background: 'none', border: `1px solid ${T.border}`, borderRadius: 4, color: T.blueLt, cursor: 'pointer' }}>All On</button>
                                <button onClick={() => setAllLayers(false)} style={{ padding: '2px 8px', fontSize: 10, fontWeight: 700, background: 'none', border: `1px solid ${T.border}`, borderRadius: 4, color: T.muted, cursor: 'pointer' }}>All Off</button>
                            </div>
                        </div>
                        <div style={{ maxHeight: 420, overflowY: 'auto', border: `1px solid ${T.border}33`, borderRadius: 6, padding: '4px 6px' }}>
                            {(() => {
                                const renderGroupRow = (group) => (
                                    <div key={group.key} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${T.border}22` }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 2 }}>
                                            <span style={{ fontSize: 10, fontWeight: 700, color: T.blueLt }}>{group.label}</span>
                                            <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                                                <button
                                                    onClick={() => handleGroupScreenshot(group)}
                                                    disabled={!!capturingGroup}
                                                    title="Isolate this group's layers and download a screenshot"
                                                    style={{
                                                        padding: '2px 5px', fontSize: 11, lineHeight: 1,
                                                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                                                        color: capturingGroup === group.key ? T.blueLt : T.muted,
                                                        cursor: capturingGroup ? 'wait' : 'pointer',
                                                    }}
                                                >
                                                    {capturingGroup === group.key ? '⏳' : '📷'}
                                                </button>
                                                <button
                                                    onClick={() => handleGroupKmz(group, '2d')}
                                                    disabled={!!exportingGroup}
                                                    title="Export this group as a flat 2D KMZ (Google Earth / GIS)"
                                                    style={{
                                                        padding: '2px 5px', fontSize: 11, lineHeight: 1,
                                                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                                                        color: exportingGroup === `${group.key}_2d` ? T.blueLt : T.muted,
                                                        cursor: exportingGroup ? 'wait' : 'pointer',
                                                    }}
                                                >
                                                    {exportingGroup === `${group.key}_2d` ? '⏳' : '🗺️'}
                                                </button>
                                                <button
                                                    onClick={() => handleGroupKmz(group, '3d')}
                                                    disabled={!!exportingGroup}
                                                    title="Export this group as a true-altitude 3D KMZ (Google Earth)"
                                                    style={{
                                                        padding: '2px 5px', fontSize: 11, lineHeight: 1,
                                                        background: 'none', border: `1px solid ${T.border}`, borderRadius: 4,
                                                        color: exportingGroup === `${group.key}_3d` ? T.blueLt : T.muted,
                                                        cursor: exportingGroup ? 'wait' : 'pointer',
                                                    }}
                                                >
                                                    {exportingGroup === `${group.key}_3d` ? '⏳' : '🌍'}
                                                </button>
                                                <button
                                                    onClick={() => setPreview3DGroup(group)}
                                                    title="Preview this group and the project obstacles in 3D, right here"
                                                    style={{
                                                        padding: '2px 5px', fontSize: 11, lineHeight: 1,
                                                        background: 'none', border: '1px solid #2ECC7144', borderRadius: 4,
                                                        color: '#2ECC71', cursor: 'pointer',
                                                    }}
                                                >
                                                    🌐
                                                </button>
                                            </div>
                                        </div>
                                        {group.layers.map(key => {
                                            const l = layerDefs.find(d => d.key === key);
                                            if (!l) return null;
                                            return (
                                                <label key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px', fontSize: 11, color: T.white, cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={!!layerVis[l.key]} onChange={() => toggleLayer(l.key)} style={{ accentColor: l.color, cursor: 'pointer' }} />
                                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: l.color, flexShrink: 0, border: l.color === '#ffffff' ? `1px solid ${T.border}` : 'none' }} />
                                                    <span style={{ opacity: layerVis[l.key] ? 1 : 0.5 }}>{l.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                );

                                const aeroGroups = screenshotGroups.filter(g => !g.desig);
                                const desigs = [...new Set(screenshotGroups.filter(g => g.desig).map(g => g.desig))];

                                return (
                                    <>
                                        {aeroGroups.length > 0 && (
                                            <div style={{ marginBottom: 6 }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, padding: '2px 2px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aerodrome</div>
                                                {aeroGroups.map(renderGroupRow)}
                                            </div>
                                        )}
                                        {desigs.map(desig => (
                                            <div key={desig} style={{ marginBottom: 6 }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: T.blue, padding: '2px 2px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>RWY {desig}</div>
                                                {screenshotGroups.filter(g => g.desig === desig).map(renderGroupRow)}
                                            </div>
                                        ))}
                                        <div style={{ paddingTop: 2 }}>
                                            <span style={{ fontSize: 10, fontWeight: 700, color: T.muted }}>Other</span>
                                            {NON_DIRECTIONAL_LAYER_DEFS.filter(l => l.key === 'transitional_debug').map(l => (
                                                <label key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px', fontSize: 11, color: T.white, cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={!!layerVis[l.key]} onChange={() => toggleLayer(l.key)} style={{ accentColor: l.color, cursor: 'pointer' }} />
                                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: l.color, flexShrink: 0, border: l.color === '#ffffff' ? `1px solid ${T.border}` : 'none' }} />
                                                    <span style={{ opacity: layerVis[l.key] ? 1 : 0.5 }}>{l.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aerodrome</div>
                    {validArps.length === 0 ? (
                        <div style={{ fontSize: 12, color: T.muted, padding: '6px 0' }}>No aerodromes within 15 km of obstacles.</div>
                    ) : (
                        <select value={icao} onChange={e => setIcao(e.target.value)} style={{ width: '100%', background: T.bg0, border: `1px solid ${T.border}`, color: T.white, padding: '6px 8px', borderRadius: 4, fontSize: 13, outline: 'none' }}>
                            {validArps.map(a => <option key={a.icao} value={a.icao}>{a.icao} — {a.name}</option>)}
                        </select>
                    )}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                    {loading ? (
                        <div style={{ color: T.muted, fontSize: 12, textAlign: 'center' }}>Loading…</div>
                    ) : error ? (
                        <div style={{ color: T.danger, fontSize: 12 }}>{error}</div>
                    ) : result ? (
                        <>
                            <div style={{ fontSize: 12, color: T.white, marginBottom: 12 }}>
                                <div style={{ fontWeight: 700 }}>{result.aerodrome.name}</div>
                                <div style={{ color: T.muted, fontSize: 11, marginTop: 2 }}>
                                    Elev {result.aerodrome.elev_m} m · ADG <b style={{ color: T.white }}>{result.aerodrome.adg || '—'}</b>
                                </div>
                                {result.horizontal && (
                                    <div style={{ color: T.muted, fontSize: 11, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${T.border}33` }}>
                                        Horizontal Surface: <b style={{ color: T.white }}>{result.horizontal.properties.height_m} m</b> above AD elev
                                    </div>
                                )}
                                {result.straight_in_instrument?.lower && (
                                    <div style={{ color: T.muted, fontSize: 11, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${T.border}33` }}>
                                        Straight-in Instrument: <b style={{ color: T.white }}>45 m</b> (lower){result.straight_in_instrument.upper && <> · <b style={{ color: T.white }}>60 m</b> (upper)</>}
                                    </div>
                                )}
                            </div>
                            {result.runway_pairs.map(p => (
                                <div key={p.pair_id} style={{ padding: 10, borderRadius: 8, background: 'rgba(34,102,238,0.08)', border: `1px solid ${T.border}`, marginBottom: 8 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.blueLt }}>RWY {p.pair_name}</div>
                                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                                        {p.classification} · Code {p.code} · {p.adg || '—'}
                                    </div>
                                    <div style={{ fontSize: 11, color: T.white, marginTop: 4 }}>
                                        Strip width: <b>{p.strip.properties.width_m} m</b>
                                        {p.strip.properties.aip_override && <span style={{ color: '#ff9800' }}> (AIP)</span>}
                                    </div>
                                    {(p.approach || []).map(feat => (
                                        <div key={feat.properties.designation} style={{ fontSize: 11, color: T.white, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${T.border}33` }}>
                                            <span style={{ color: T.muted }}>Approach {feat.properties.designation}:</span>{' '}
                                            <b>{feat.properties.inner_w_m} m</b> inner · {feat.properties.length_m} m @ {feat.properties.slope_pct}%
                                            {feat.properties.aip_override && <span style={{ color: '#ff9800' }}> (AIP)</span>}
                                        </div>
                                    ))}
                                    {p.transitional?.[0] && (
                                        <div style={{ fontSize: 11, color: T.white, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${T.border}33` }}>
                                            <span style={{ color: T.muted }}>Transitional:</span>{' '}
                                            <b>{p.transitional[0].properties.slope_pct}%</b> slope (both sides)
                                        </div>
                                    )}
                                    {Object.entries(p.ofz || {}).map(([desig, surfs]) => (
                                        <div key={desig} style={{ fontSize: 11, color: T.white, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${T.border}33` }}>
                                            <span style={{ color: T.muted }}>OFZ {desig}:</span>{' '}
                                            {Object.keys(surfs).map(k => k.replace('_', ' ')).join(', ')}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div style={{ color: T.muted, fontSize: 12 }}>No data.</div>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                    <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />

                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 100, display: 'flex', gap: 4, background: T.bg1, padding: 4, borderRadius: 8, border: `1px solid ${T.border}` }}>
                        {Object.entries(TILE_LAYERS).map(([k, l]) => (
                            <button
                                key={k}
                                onClick={() => setMapMode(k)}
                                style={{
                                    padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, cursor: 'pointer',
                                    background: mapMode === k ? 'rgba(58, 138, 196, 0.15)' : 'transparent',
                                    color: mapMode === k ? T.blueLt : T.muted, fontWeight: mapMode === k ? 700 : 400
                                }}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                        penetratingCount={adgShieldRealPenetration.size}
                        onExportPenetrating={() => {
                            const byId = new Map();
                            [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                             ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                                byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                            });
                            const entries = [];
                            adgShieldRealPenetration.forEach((penList, obsId) => {
                                const r = byId.get(obsId);
                                if (!r) return;
                                penList.forEach(v => entries.push({
                                    r,
                                    surfaceLabel: ADG_SURFACE_LABELS[v.surfaceType] + (v.runway ? ` RWY ${v.runway}` : ''),
                                    penetrationAmount: (-v.margin).toFixed(2),
                                }));
                            });
                            exportSurfacePenetratingCsv(entries, radiusKm);
                        }} />
                    {shieldOn && hasAdgInclinedImpact && (
                        <div style={{ position: 'absolute', top: 226, right: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 8, border: '1px solid #FFB02055', backdropFilter: 'blur(8px)', maxWidth: 260, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                            <div style={{ color: '#FFB020', fontWeight: 700, marginBottom: 2 }}>🛫 Inclined Surfaces (real check)</div>
                            <div style={{ marginBottom: 4 }}>
                                Project impact on: {[...new Set(Object.keys(adgSurfaceImpacts).map(k => k.split('_')[0]))].map(t => ADG_SURFACE_LABELS[t]).join(', ')}
                            </div>
                            {adgShieldLoading ? 'Checking real penetration…' : (
                                adgShieldVerdicts.size > 0
                                    ? <span><b style={{ color: '#FFB020' }}>{adgShieldVerdicts.size}</b> obstacle(s) confirmed to already penetrate as much or more than this project's worst case on at least one surface — not a new impact there.</span>
                                    : 'No candidate already penetrates any of these surfaces at least as much as this project — flat elevation comparison alone would be misleading here.'
                            )}
                        </div>
                    )}
                    <MapMeasureTool map={mapState} top={52} right={12} tableOffset={resultsCollapsed ? 40 : resultsHeight} />
                    <MapCoordinateViewer map={mapState} />
                </div>

                <div style={{ height: resultsCollapsed ? 38 : resultsHeight, flexShrink: 0, borderTop: `1px solid ${T.border}`, background: T.bg1, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden' }}>
                    {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                    <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}33`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Obstacle Assessment
                            </span>
                            <span style={{ fontSize: 11, color: T.muted }}>
                                {assessing ? 'Running…' : `${assessResults.length} result${assessResults.length !== 1 ? 's' : ''} · ${obstacleList.length} obstacle${obstacleList.length !== 1 ? 's' : ''}`}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            {assessResults.length > 0 && (
                                <button
                                    onClick={() => {
                                        const headers = ["Obstacle", "Aerodrome", "Runway", "Surface", "Surface Elev (m)", "Obstacle Elev (m)", "Penetration (m)", "Status"];
                                        const rows = assessResults.map(r => [
                                            r.obstacle_name,
                                            icao,
                                            r.runway,
                                            r.surface,
                                            r.surf_elev_amsl ?? '-',
                                            r.obs_elev ?? '-',
                                            r.margin != null ? (-r.margin).toFixed(2) : '-',
                                            r.status,
                                        ]);
                                        exportToCsv(headers, rows, `OLS_ADG_Assessment_Results_${icao || 'aerodrome'}.csv`);
                                    }}
                                    style={{
                                        padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                        background: T.blue, color: T.white, fontSize: 10, fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: 4,
                                    }}
                                >
                                    📥 Export CSV
                                </button>
                            )}
                            {!resultsPoppedOut && (
                                <button
                                    onClick={() => { setResultsPoppedOut(true); setResultsCollapsed(true); }}
                                    style={{ background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                                    title="Pop out results to a movable window"
                                >
                                    🗖 Pop Out
                                </button>
                            )}
                            <button
                                onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                style={{ background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                            >
                                {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                            </button>
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {resultsPoppedOut ? (
                            <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                Results are popped out.
                                <button
                                    onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }}
                                    style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                >
                                    Bring back
                                </button>
                            </div>
                        ) : renderResultsContent()}
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - OLS (ADG) Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={900}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}

            {preview3DGroup && (
                <Cesium3DPreviewModal
                    title={`${icao} — ADG ${preview3DGroup.backendGroup} 3D Preview`}
                    kmlUrl={`${import.meta.env.VITE_API_URL || '/api'}/ols-adg-surfaces/${icao}/kml/${preview3DGroup.backendGroup}?mode=3d${preview3DGroup.desig ? `&desig=${encodeURIComponent(preview3DGroup.desig)}` : ''}${project?.id ? `&project_id=${project.id}` : ''}`}
                    onClose={() => setPreview3DGroup(null)}
                />
            )}
        </div>
    );
}
